import { useState } from 'react';
import { Popover } from 'antd';
import { InfoCircleOutlined, DownOutlined } from '@ant-design/icons';

// 图片模型详细计费规则（全部从 metadata 推导，不写死模型名）。
// 新增模型只要配置了 pixel_pricing / capabilities.layer_decomposition，这里自动展示。
const parseMeta = (model) => {
  if (!model || !model.metadata) return {};
  try {
    return typeof model.metadata === 'string' ? JSON.parse(model.metadata) : model.metadata;
  } catch (e) {
    return {};
  }
};

const fmtNum = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
};

const fmtPx = (px) => {
  const m = Number(px) / 1000000;
  return `${Number.isInteger(m) ? m : Number(m.toFixed(2))}M`;
};

const BillingRules = ({ model, lang = 'en', compact = false }) => {
  const [open, setOpen] = useState(false);
  const meta = parseMeta(model);
  const rawTiers = Array.isArray(meta.pixel_pricing) ? meta.pixel_pricing : [];
  const tiers = [...rawTiers]
    .filter((x) => x && Number(x.price) > 0)
    .sort((a, b) => (a.max_pixels || Number.MAX_SAFE_INTEGER) - (b.max_pixels || Number.MAX_SAFE_INTEGER));
  if (tiers.length === 0) return null;

  const layer = meta.capabilities?.layer_decomposition;
  const layerEnabled = !!layer?.enabled;
  const multiplier = Number(layer?.billing?.price_multiplier) > 0 ? Number(layer.billing.price_multiplier) : 1;
  const inputPrice = Number(meta.input_image_price) > 0 ? Number(meta.input_image_price) : 0;
  const inputFree = meta.input_images_free != null ? Math.max(0, Number(meta.input_images_free)) : 1;
  const maxOutputs = Number(layer?.billing?.max_outputs) || (1 + (Number(layer?.output?.max_layers) || 16));

  const isZh = lang === 'zh';
  const tr = (zh, en) => (isZh ? zh : en);
  const unit = tr('张', 'image');

  const tierLabel = (tier, i) => {
    if (tier.max_pixels) return `≤ ${fmtPx(tier.max_pixels)} px`;
    const prev = tiers[i - 1];
    return prev && prev.max_pixels ? `> ${fmtPx(prev.max_pixels)} px` : tr('不限', 'Any');
  };

  const priceRow = (label, price, key) => (
    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, lineHeight: 1.7 }}>
      <span style={{ color: '#595959' }}>{label}</span>
      <span style={{ color: '#722ed1', fontWeight: 600, whiteSpace: 'nowrap' }}>
        ${fmtNum(price)} / {unit}
      </span>
    </div>
  );

  const sectionTitle = (text) => (
    <div style={{ fontWeight: 600, color: '#262626', margin: '9px 0 2px' }}>{text}</div>
  );

  let example = null;
  if (layerEnabled && tiers.length > 1) {
    const low = Number(tiers[0].price);
    const high = Number(tiers[tiers.length - 1].price);
    const lowLayer = low * multiplier;
    const highLayer = high * multiplier;
    const nLayers = Math.min(6, Math.max(0, maxOutputs - 1));
    const total = highLayer + nLayers * lowLayer;
    example = tr(
      `例：1 张高档底图 + ${nLayers} 张低档图层 = $${fmtNum(highLayer)} + ${nLayers} × $${fmtNum(lowLayer)} = $${fmtNum(total)}`,
      `Example: 1 high-tier base + ${nLayers} low-tier layers = $${fmtNum(highLayer)} + ${nLayers} × $${fmtNum(lowLayer)} = $${fmtNum(total)}`
    );
  } else {
    const low = Number(tiers[0].price);
    example = tr(
      `例：2 张低档输出 = 2 × $${fmtNum(low)} = $${fmtNum(low * 2)}`,
      `Example: 2 low-tier outputs = 2 × $${fmtNum(low)} = $${fmtNum(low * 2)}`
    );
  }

  const content = (
    <div style={{ width: 330, maxWidth: 'calc(100vw - 48px)', fontSize: 12, color: '#333', lineHeight: 1.65 }}>
      {sectionTitle(tr('普通生成', 'Standard generation'))}
      <div style={{ color: '#8c8c8c', fontSize: 11 }}>
        {tr('按每张成功输出的实际宽×高落档：', 'Each successful output is tiered by its actual width × height:')}
      </div>
      {tiers.map((tier, i) => priceRow(tierLabel(tier, i), tier.price, `n${i}`))}

      {layerEnabled && (
        <>
          {sectionTitle(tr('图层拆分', 'Layer decomposition'))}
          <div style={{ color: '#8c8c8c', fontSize: 11 }}>
            {tr(
              `底图 + 每个图层分别计费，每张为同档普通价的 ${Math.round(multiplier * 100)}%：`,
              `Base + each layer billed separately, each at ${Math.round(multiplier * 100)}% of the normal tier price:`
            )}
          </div>
          {tiers.map((tier, i) => priceRow(tierLabel(tier, i), Number(tier.price) * multiplier, `l${i}`))}
          <div style={{ color: '#8c8c8c', fontSize: 11 }}>
            {tr(
              `最多 ${maxOutputs} 个输出（1 张底图 + ${maxOutputs - 1} 个图层）。`,
              `Up to ${maxOutputs} outputs (1 base + ${maxOutputs - 1} layers).`
            )}
          </div>
        </>
      )}

      {sectionTitle(tr('参考图', 'Reference images'))}
      {inputPrice > 0 ? (
        <div>
          {inputFree > 0
            ? tr(
                `前 ${inputFree} 张免费；第 ${inputFree + 1} 张起 $${fmtNum(inputPrice)}/张。`,
                `First ${inputFree} free; $${fmtNum(inputPrice)}/image after that.`
              )
            : tr(`$${fmtNum(inputPrice)}/张。`, `$${fmtNum(inputPrice)}/image.`)}{' '}
          {tr('参考图费用按请求计一次，不随输出张数倍增。', 'Charged once per request, not multiplied by the number of outputs.')}
        </div>
      ) : (
        <div>{tr('不收取参考图费用。', 'No reference image fee.')}</div>
      )}
      {layerEnabled && (
        <div style={{ color: '#8c8c8c', fontSize: 11 }}>
          {tr('图层拆分固定 1 张输入图，因此不收参考图费。', 'Layer decomposition uses exactly one input image, so there is no input fee.')}
        </div>
      )}

      {sectionTitle(tr('计算方式', 'Formula'))}
      <div
        style={{
          fontFamily: 'monospace',
          fontSize: 11,
          background: '#fafafa',
          border: '1px solid #f0f0f0',
          borderRadius: 4,
          padding: '5px 8px',
        }}
      >
        {layerEnabled
          ? tr(
              `总价 = Σ(每张输出实际像素档普通单价 × ${multiplier})`,
              `Total = Σ(normal tier price of each output's actual pixels × ${multiplier})`
            )
          : tr(
              `总价 = Σ(每张输出实际像素档单价)${
                inputPrice > 0 ? ` + max(参考图数 − ${inputFree}, 0) × $${fmtNum(inputPrice)}` : ''
              }`,
              `Total = Σ(tier price of each output's actual pixels)${
                inputPrice > 0 ? ` + max(input images − ${inputFree}, 0) × $${fmtNum(inputPrice)}` : ''
              }`
            )}
      </div>
      <div style={{ color: '#8c8c8c', fontSize: 11, marginTop: 6 }}>
        {tr('失败图片不计费；按实际成功输出结算。', 'Failed outputs are not billed; only successful outputs are charged.')}
      </div>
      {example && <div style={{ color: '#8c8c8c', fontSize: 11, marginTop: 4 }}>{example}</div>}
    </div>
  );

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement={compact ? 'bottomRight' : 'bottomLeft'}
      title={<span style={{ fontSize: 13, fontWeight: 600 }}>{tr('详细计费规则', 'Detailed billing')}</span>}
      content={content}
      overlayStyle={{ maxWidth: 380 }}
    >
      <span
        className="billing-rules-trigger"
        role="button"
        tabIndex={0}
        aria-label={tr('计费规则', 'Billing rules')}
        aria-expanded={open}
        title={tr('查看详细计费规则', 'View detailed billing rules')}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          cursor: 'pointer',
          color: open ? '#722ed1' : '#8c8c8c',
          fontSize: compact ? 11 : 12,
          whiteSpace: 'nowrap',
          userSelect: 'none',
          marginTop: compact ? 0 : 4,
        }}
      >
        <InfoCircleOutlined style={{ fontSize: compact ? 12 : 13 }} />
        <span>{tr('计费规则', 'Billing rules')}</span>
        <DownOutlined style={{ fontSize: 9, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
      </span>
    </Popover>
  );
};

export default BillingRules;
